import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import { IDoesFilterPassParams, IFilterParams } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { forwardRef, useCallback, useImperativeHandle } from 'react';

import { TableMutliselect } from '../../../../../components/DropdownMenu/options/TableMultiselect';
import { IFundMetricsByFund } from '../../../../../data-models/fund-metrics.data-model';
import { IMetricsDataModel } from '../../../../../data-models/metrics.data-model';
import { COMPANY_VIEW_TYPE, ViewTypeByData } from '../../../../../data-models/view-config.data-model';
import { MetricsTransactionDataModel } from '../../../../../schemas/MetricsTransaction.schema';
import { selectableFundsAtom } from '../../../../../services/state/AppConfigStateJ';
import { useFundSelector } from '../../../hooks/useFundSelector';
import { useUpdateSelectedView } from '../../../hooks/useViewActions';
import {
  activeFundsStateJ,
  portfolioAllFundsSelectedStateJ,
  areNoFundsSelectedStateJ,
  portfolioFundOptionsStateJ,
  includeNonParticipatingRoundsStateJ,
  selectedFundsAsOptionsPFStateJ,
} from '../../../state/FundFilterStateJ';
import { selectedViewPFJ } from '../../../state/ViewStateJ';

export const FundFilterComponent = forwardRef(function FundFilterComponent(_props: IFilterParams, ref) {
  const view = useAtomValue(selectedViewPFJ);
  const allFunds = useAtomValue(selectableFundsAtom);
  const options = useAtomValue(portfolioFundOptionsStateJ);
  const activeFunds = useAtomValue(activeFundsStateJ);
  const selected = useAtomValue(selectedFundsAsOptionsPFStateJ);
  const hasAllSelected = useAtomValue(portfolioAllFundsSelectedStateJ);
  const hasNoneSelected = useAtomValue(areNoFundsSelectedStateJ);
  const includeNonParticipatingRounds = useAtomValue(includeNonParticipatingRoundsStateJ);
  const updateSelectedView = useUpdateSelectedView();

  const isRoundTracker = view?.companyViewType === COMPANY_VIEW_TYPE.ROUND_TRACKER;

  const { onSelect, onSelectAll, onSelectDeselectAll } = useFundSelector();

  const handleToggleIncludeNonParticipating = useCallback(() => {
    updateSelectedView({ includeNonParticipatingRounds: !includeNonParticipatingRounds });
    _props.filterChangedCallback();
  }, [includeNonParticipatingRounds, updateSelectedView, _props]);

  useImperativeHandle(ref, () => {
    if (isRoundTracker) {
      return {
        doesFilterPass: () => true,
        isFilterActive: () => !hasAllSelected || !includeNonParticipatingRounds,
      };
    }
    return {
      doesFilterPass(params: IDoesFilterPassParams) {
        if (!params.node?.data || !view) {
          return false;
        }

        if (view?.viewType === ViewTypeByData.fund) {
          return activeFunds?.some((f) => f.id === (params.node.data as IFundMetricsByFund).fundId);
        }

        const fundIdsMap: Record<number, number> = (
          params.node.data as IMetricsDataModel
        ).transactions.reduce(
          (map: Record<number, number>, t: MetricsTransactionDataModel) => {
            if (t.fundId != null) {
              return { ...map, [t.fundId]: t.fundId };
            } else {
              return map;
            }
          },
          {} as Record<number, number>
        );

        return activeFunds?.some((f) => !!fundIdsMap[f.id]);
      },
      isFilterActive() {
        return !!activeFunds && !!allFunds;
      },
    };
  });

  const handleSelectDeselectAll = useCallback(() => {
    if (hasAllSelected) {
      onSelectDeselectAll();
    } else {
      onSelectAll();
    }
  }, [hasAllSelected, onSelectAll, onSelectDeselectAll]);

  const nonParticipatingToggle = isRoundTracker ? (
    <FormControlLabel
      label={<Typography variant='body2'>Include Non Participating</Typography>}
      sx={{ display: 'flex', ml: '-0.25rem', my: '0.25rem' }}
      control={
        <Checkbox
          size='small'
          checked={includeNonParticipatingRounds}
          onChange={handleToggleIncludeNonParticipating}
        />
      }
    />
  ) : undefined;

  if (!allFunds) {
    return null;
  }

  return (
    <TableMutliselect
      items={options}
      onItemSelect={onSelect}
      isSearchable
      selectedItems={selected}
      onClearFilters={onSelectAll}
      selectAllNoneOptions={{
        onSelectAllNone: handleSelectDeselectAll,
        allSelected: hasAllSelected,
        indeterminate: !hasAllSelected && !hasNoneSelected,
      }}
      footerContent={nonParticipatingToggle}
    />
  );
});
